import React, { useState, useEffect } from 'react';

const App: React.FC = () => {
  const [activeApp, setActiveApp] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFBFC] overflow-x-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
        :root {
          --font-display: 'Playfair Display', Georgia, serif;
          --font-body: 'DM Sans', system-ui, sans-serif;
          --color-primary: #33A0D9;
          --color-calm: #66BF99;
          --color-warm: #E6804D;
          --color-deep: #9980B3;
          --color-veterans: #2D5A3D;
          --color-signal: #7C3AED;
          --color-guide: #D97706;
          --color-threshold: #3D8B8B;
          --color-micra: #7EC8A4;
        }
        .font-display { font-family: var(--font-display); }
        .font-body { font-family: var(--font-body); }
        @keyframes breathe { 0%, 100% { transform: scale(1); opacity: 0.6; } 50% { transform: scale(1.1); opacity: 0.8; } }
        @keyframes fade-up { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes gradient-shift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        .animate-breathe { animation: breathe 8s ease-in-out infinite; }
        .animate-fade-up { animation: fade-up 0.8s ease-out forwards; }
        .animate-gradient { background-size: 300% 300%; animation: gradient-shift 15s ease infinite; }
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .app-card { transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
        .app-card:hover { transform: translateY(-8px); }
        .grain { background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); opacity: 0.03; }
      `}</style>
      <div className="fixed inset-0 grain pointer-events-none z-50" />

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${scrollY > 50 ? 'py-3 md:py-4 bg-white/95 backdrop-blur-xl shadow-sm' : 'py-4 md:py-6 bg-white/80 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center">
          <div className="flex items-center gap-2 md:gap-4">
            <div className="relative w-10 h-10 md:w-12 md:h-12">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--color-primary)] via-[var(--color-calm)] to-[var(--color-deep)] animate-breathe" />
              <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 md:w-6 md:h-6 text-[var(--color-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4" strokeLinecap="round" /></svg>
              </div>
            </div>
            <div>
              <span className="font-display text-xl md:text-2xl font-bold text-gray-900 tracking-tight">Mind Relief</span>
              <span className="font-body text-[8px] md:text-[10px] font-semibold text-gray-400 uppercase tracking-[0.2em] block -mt-1">Studios, LLC</span>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-10">
            <a href="#philosophy" className="font-body text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors">Philosophy</a>
            <a href="#apps" className="font-body text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors">Apps</a>
            <a href="#founder" className="font-body text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors">About</a>
            <a href="#legal" className="font-body text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors">Privacy</a>
            <a href="#apps" className="px-5 py-2.5 bg-gray-900 text-white rounded-full font-body text-sm font-semibold hover:bg-gray-800 transition-colors flex items-center gap-2">Browse Apps<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg></a>
          </nav>
          <a href="#apps" className="md:hidden px-4 py-2 bg-gray-900 text-white rounded-full font-body text-xs font-semibold">Apps</a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 md:pt-0">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-[800px] h-[800px] rounded-full animate-breathe" style={{ background: 'radial-gradient(circle, rgba(51,160,217,0.15) 0%, transparent 70%)', top: '-20%', right: '-10%' }} />
          <div className="absolute w-[600px] h-[600px] rounded-full animate-breathe" style={{ background: 'radial-gradient(circle, rgba(102,191,153,0.15) 0%, transparent 70%)', bottom: '-10%', left: '-5%', animationDelay: '2s' }} />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-8 text-center">
          <div className="animate-fade-up opacity-0 mb-8">
            <span className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-white shadow-lg shadow-gray-200/50 border border-gray-100">
              <span className="w-5 h-5 md:w-6 md:h-6 rounded-md bg-[var(--color-primary)]/10 flex items-center justify-center text-[var(--color-primary)] text-[10px] md:text-xs font-bold">Rx</span>
              <span className="font-body text-xs md:text-sm font-semibold text-gray-600">Built by a Board-Certified Anesthesiologist · 30 Years of Clinical Practice</span>
            </span>
          </div>
          <h1 className="animate-fade-up opacity-0 delay-100 font-display text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight leading-[1.1] mb-8">Mental Health Apps That<br /><span className="text-transparent bg-clip-text animate-gradient italic inline-block pr-4" style={{ backgroundImage: 'linear-gradient(135deg, var(--color-primary), var(--color-calm), var(--color-deep), var(--color-warm), var(--color-primary))', backgroundSize: '300% 300%' }}>Actually Help</span></h1>
          <p className="animate-fade-up opacity-0 delay-200 font-body text-lg md:text-xl text-gray-500 max-w-3xl mx-auto mb-4 leading-relaxed">Designed from real clinical gaps — what patients struggle to understand, what gets lost between appointments, what's missing between <span className="font-semibold text-gray-700">diagnosis and care.</span></p>
          <p className="animate-fade-up opacity-0 delay-200 font-body text-lg md:text-xl text-gray-500 max-w-3xl mx-auto mb-10">No subscriptions. No data collection. No catch.</p>
          <div className="animate-fade-up opacity-0 delay-300 flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a href="#apps" className="group relative px-8 py-4 bg-gray-900 text-white rounded-full font-body font-semibold text-lg overflow-hidden transition-all hover:shadow-xl flex items-center justify-center gap-2"><span className="relative z-10">Browse the Apps — Most Are Free</span><svg className="w-5 h-5 relative z-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg></a>
            <a href="#founder" className="px-8 py-4 bg-white text-gray-700 rounded-full font-body font-semibold text-lg border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all">About the Founder</a>
          </div>
          <div className="animate-fade-up opacity-0 delay-300 flex flex-wrap justify-center gap-6 md:gap-8 text-sm text-gray-400">
            <div className="flex items-center gap-2"><svg className="w-4 h-4 text-[var(--color-primary)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg><span className="font-body">No accounts required</span></div>
            <div className="hidden md:block w-px h-4 bg-gray-200" />
            <div className="flex items-center gap-2"><svg className="w-4 h-4 text-[var(--color-calm)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg><span className="font-body">All data stays on your device</span></div>
            <div className="hidden md:block w-px h-4 bg-gray-200" />
            <div className="flex items-center gap-2"><svg className="w-4 h-4 text-[var(--color-deep)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg><span className="font-body">Education only — never diagnosis</span></div>
            <div className="hidden md:block w-px h-4 bg-gray-200" />
            <div className="flex items-center gap-2"><svg className="w-4 h-4 text-[var(--color-warm)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg><span className="font-body">No advertising. Ever.</span></div>
          </div>
        </div>
      </section>

      {/* Clinical Philosophy */}
      <section className="py-20 px-8 bg-white border-y border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">Our Clinical Philosophy</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[var(--color-primary)]/10 flex items-center justify-center"><svg className="w-8 h-8 text-[var(--color-primary)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg></div>
              <h3 className="font-display text-xl font-bold text-gray-900 mb-2">Education, not diagnosis</h3>
              <p className="font-body text-gray-500 text-sm">We provide information to help you understand — never to label or prescribe.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[var(--color-calm)]/10 flex items-center justify-center"><svg className="w-8 h-8 text-[var(--color-calm)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></div>
              <h3 className="font-display text-xl font-bold text-gray-900 mb-2">Clarity, not overwhelm</h3>
              <p className="font-body text-gray-500 text-sm">Every screen is designed to reduce confusion, not add to it.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[var(--color-deep)]/10 flex items-center justify-center"><svg className="w-8 h-8 text-[var(--color-deep)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg></div>
              <h3 className="font-display text-xl font-bold text-gray-900 mb-2">The patient owns their information</h3>
              <p className="font-body text-gray-500 text-sm">Your data stays on your device. Always.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Apps Section */}
      <section id="apps" className="py-32 px-8 bg-[#FAFBFC]">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-20">
            <span className="font-body text-sm font-bold text-[var(--color-primary)] uppercase tracking-[0.2em] mb-4 block">The Collection</span>
            <h2 className="font-display text-5xl md:text-6xl font-bold text-gray-900 tracking-tight mb-6">Apps Built from<br />Clinical Experience</h2>
            <p className="font-body text-xl text-gray-500 leading-relaxed">Each application addresses a gap observed in direct patient care.</p>
          </div>
          <div className="space-y-6">
            <MoodQuadCard activeApp={activeApp} setActiveApp={setActiveApp} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <CareGuideCard activeApp={activeApp} setActiveApp={setActiveApp} />
              <TreatmentFinderCard activeApp={activeApp} setActiveApp={setActiveApp} />
            </div>
            <SignalWellCard activeApp={activeApp} setActiveApp={setActiveApp} />
            <VeteransCard activeApp={activeApp} setActiveApp={setActiveApp} />
            <ThresholdCard activeApp={activeApp} setActiveApp={setActiveApp} />
            <MicraCard activeApp={activeApp} setActiveApp={setActiveApp} />
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section id="founder" className="py-32 px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-body text-sm font-bold text-[var(--color-calm)] uppercase tracking-[0.2em] mb-4 block">About</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-6">Why a Physician Built These Tools</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            <div className="lg:col-span-1 flex flex-col items-center">
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden shadow-xl shadow-gray-200/50 mb-4"><img src="/founder.jpg" alt="Dr. Bruce Nadro" className="w-full h-full object-cover" /></div>
              <p className="font-display text-lg font-bold text-gray-900">Built by Dr. Bruce Nadro</p>
              <p className="font-body text-sm text-gray-500">Founder, Mind Relief Studios, LLC</p>
            </div>
            <div className="lg:col-span-2 space-y-6">
              <p className="font-body text-xl text-gray-600 leading-relaxed">"After 30 years of clinical practice and 7 years specializing in ketamine therapy, I kept seeing the same gap — patients who were motivated, engaged, and trying hard, but missing the tools to understand their options, track their journey, and communicate their history effectively."</p>
              <p className="font-body text-xl text-gray-600 leading-relaxed">Mind Relief Studios, LLC was built to fill that gap. Not with clinical tools — but with educational companions that help people show up to appointments better prepared and leave with more clarity.</p>
              <p className="font-body text-xl text-gray-600 leading-relaxed">These aren't wellness trends or startup ideas. They're answers to problems I watched patients struggle with every day.</p>
              <div className="p-6 rounded-2xl bg-gray-50 border border-gray-100 mt-8">
                <h3 className="font-display text-lg font-bold text-gray-900 mb-4">Credentials</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3"><div className="w-2 h-2 rounded-full bg-[var(--color-primary)] mt-2" /><span className="font-body text-gray-600">Board Certified Anesthesiologist</span></li>
                  <li className="flex items-start gap-3"><div className="w-2 h-2 rounded-full bg-[var(--color-calm)] mt-2" /><span className="font-body text-gray-600">National lecturer</span></li>
                  <li className="flex items-start gap-3"><div className="w-2 h-2 rounded-full bg-[var(--color-deep)] mt-2" /><span className="font-body text-gray-600">Co-Founder, Mindrf.org</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section id="philosophy" className="py-32 px-8 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30"><div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`, backgroundSize: '32px 32px' }} /></div>
        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <span className="font-body text-sm font-bold text-[var(--color-calm)] uppercase tracking-[0.2em] mb-4 block">Our Philosophy</span>
            <h2 className="font-display text-5xl md:text-6xl font-bold text-white tracking-tight mb-6">Mental Health Apps<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-calm)] to-[var(--color-primary)]">Should Actually Help</span></h2>
            <p className="font-body text-xl text-gray-400 max-w-2xl mx-auto">We believe that a mental health app should actually help. Our apps are designed to help you understand and improve your emotional well-being.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <PrincipleCard number="01" title="Science-Backed" description="Every app is built on established psychological frameworks — not wellness trends or guesswork." color="var(--color-primary)" />
            <PrincipleCard number="02" title="Privacy First" description="Your data stays on your device. We don't sell, share, or monetize your most personal information." color="var(--color-calm)" />
            <PrincipleCard number="03" title="Made With Care" description="Designed by a physician who understands mental health struggles firsthand. Built with empathy." color="var(--color-warm)" />
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-8 bg-[#FAFBFC] border-t border-gray-100">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">Our Commitment to Responsible Design</h2>
          <p className="font-body text-gray-500 leading-relaxed">Every app in the Mind Relief Studios, LLC family is designed for education and self-awareness only. None provide medical advice, diagnosis, or treatment. These tools are designed to complement — never replace — the patient-provider relationship.</p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-8 bg-white relative">
        <div className="max-w-xl mx-auto text-center">
          <span className="font-body text-sm font-bold text-gray-400 uppercase tracking-[0.2em] mb-4 block">Stay Connected</span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-6">Join Our Journey</h2>
          <p className="font-body text-lg text-gray-500 mb-10">Get updates on new apps, features, and mental health resources.</p>
          <form 
            onSubmit={async (e) => { 
              e.preventDefault(); 
              if (email && email.includes('@')) { 
                try {
                  const formData = new FormData();
                  formData.append('EMAIL', email);
                  formData.append('email_address_check', '');
                  formData.append('locale', 'en');
                  formData.append('html_type', 'simple');
                  await fetch('https://0b02b0c0.sibforms.com/serve/MUIFAA0Kops4550Zk_uzzuEiSZQ_oIHKuVJ2Mn7Y7uMmYfaG9e4JN9JsjLjwxFHig7sSDPHXgHsmCCxl4T4xrXvCQr8PnyYkKXrWemI8tJushfJ4lKeUC-7IWmMGOEdrPuNG9kcdHN3Zm9dks4ncQnJcjC1BXcMZq_1MjFc0R-h97pAX_iUC1vt5FLrUym2uZ6VZ16bQCNemFDWQ5w==', {
                    method: 'POST',
                    body: formData,
                    mode: 'no-cors',
                  });
                  setFormSubmitted(true); 
                  setEmail('');
                  setTimeout(() => setFormSubmitted(false), 5000);
                } catch (err) {
                  console.error('Subscription error:', err);
                }
              } 
            }} 
            className="space-y-4"
          >
            {!formSubmitted ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required className="flex-1 px-6 py-4 bg-gray-50 border border-gray-200 rounded-2xl font-body text-gray-900 placeholder:text-gray-400 focus:border-[var(--color-primary)] focus:outline-none transition-all" />
                <button type="submit" className="px-8 py-4 bg-gray-900 text-white rounded-2xl font-body font-semibold hover:bg-gray-800 transition-colors">Subscribe</button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-3 px-6 py-4 bg-[var(--color-calm)]/10 border border-[var(--color-calm)]/20 rounded-2xl">
                <svg className="w-5 h-5 text-[var(--color-calm)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" /></svg>
                <span className="font-body text-gray-700">Welcome aboard!</span>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Legal Section */}
      <section id="legal" className="py-20 px-8 bg-[#1a1a2e]">
        <div className="max-w-4xl mx-auto">
          <span className="font-body text-sm font-bold text-gray-500 uppercase tracking-[0.2em] mb-8 block">Legal</span>
          <div className="space-y-4">
            {/* Privacy Statement */}
            <div>
              <button onClick={() => setActiveApp(activeApp === 'privacy' ? null : 'privacy')} className="w-full flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <span className="font-display text-xl font-bold text-gray-300">Privacy Statement</span>
                <svg className={`w-5 h-5 text-gray-400 transition-transform ${activeApp === 'privacy' ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
              </button>
              {activeApp === 'privacy' && (
                <div className="p-6 rounded-xl bg-white/5 animate-fade-up">
                  <div className="prose prose-invert max-w-none space-y-6">
                    <p className="font-body text-gray-400 text-sm">Last Modified Date: March 13, 2026</p>
                    <p className="font-body text-gray-300">Mind Relief Studios, LLC ("Mind Relief Studios, LLC") believes information about your mental health journey is yours and should remain that way. This Privacy Statement governs our collection and use of personal and non-personal data with respect to the Apps. We have a privacy-first approach that prioritizes data minimization and enables you to research your mental health status and treatment options while protecting your privacy. Our suite of mental health apps (the "Apps"), including but not limited to Treatment Finder, Mental Health Care Guide, Threshold, and SignalWell, do not collect or share your personal data with any third parties. Any personal information you enter into the Apps is stored locally on your device and is not transmitted to Mind Relief Studios, LLC.</p>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">What this means for you:</h4>
                      <ul className="font-body text-gray-400 space-y-1">
                        <li>• No personal information (name, email, phone number, etc.) is required to use the Apps;</li>
                        <li>• No user accounts are created within the Apps; and</li>
                        <li>• No data from the Apps is transmitted to third-party servers.</li>
                      </ul>
                    </div>
                    
                    <p className="font-body text-gray-400">You may have the option to enter your name into the Apps if you choose. This data remains on your device and is not transmitted to Mind Relief Studios, LLC. Data you create in the Apps (such as check-in history, voice baseline values, and reminder preferences) is stored only on your device. Mind Relief Studios, LLC does not receive, access, or store this data. Some of the Apps, such as SignalWell and Threshold, may utilize voice or audio input as part of their core functionality. Any voice or audio data collected within these Apps is processed and stored exclusively on your device. No audio recordings, voice data, or derived summaries are ever transmitted to or stored on any external server. We never receive, access, or store voice or audio input.</p>
                    
                    <p className="font-body text-gray-400">The Apps do not use analytics, tracking tools, or third-party advertising services. We do not collect usage data, crash reports tied to personal identifiers, or behavioral data of any kind.</p>
                    
                    <p className="font-body text-gray-300 italic">This privacy-first approach is intentional and reflects Mind Relief Studios, LLC's mission to provide neutral, educational resources to support you.</p>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">Non-Personal Data Collected by the Apps</h4>
                      <p className="font-body text-gray-400">The Apps may collect non-personal data, such as onboarding data, when you install one of the Apps (an operating system signal that the App has been installed). The Apps may also collect numerical summaries from your voice or mood check-ins, which are not tied to any personal information. This information is used solely to provide services within the Apps and maintain functionality, and remains on your device. This information is not sent to any third-party servers or to Mind Relief Studios, LLC.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">User-Initiated Data Export</h4>
                      <p className="font-body text-gray-400">Some Mind Relief Studios, LLC Apps may allow you to print or export a PDF version of data you have personally entered into the Apps. This export feature is available solely at your request and is initiated only by you. Mind Relief Studios, LLC does not initiate, access, receive, or retain any data generated through this export feature, and we are not able to collect the data you have personally entered into the Apps.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">Children's Privacy</h4>
                      <p className="font-body text-gray-400">Our Apps are not directed to children under the age of 13. We do not knowingly collect any information from children under 13. If you believe a child has provided information through one of our Apps, please contact us, and we will take commercially reasonable actions to delete that data.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">Changes to This Statement</h4>
                      <p className="font-body text-gray-400">We reserve the right to update this Privacy Statement at any time. Any changes will be reflected by an updated Effective Date above. Continued use of any Mind Relief Studios, LLC Apps following a policy update constitutes acceptance of the revised policy.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">Contact</h4>
                      <p className="font-body text-gray-400">For questions or concerns regarding this Privacy Statement, please contact: Mind Relief Studios, LLC. <a href="mailto:info@mindrelief.app" className="text-[var(--color-primary)] hover:underline">info@mindrelief.app</a></p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Terms of Service */}
            <div>
              <button onClick={() => setActiveApp(activeApp === 'terms' ? null : 'terms')} className="w-full flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <span className="font-display text-xl font-bold text-gray-300">Terms of Service</span>
                <svg className={`w-5 h-5 text-gray-400 transition-transform ${activeApp === 'terms' ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
              </button>
              {activeApp === 'terms' && (
                <div className="p-6 rounded-xl bg-white/5 animate-fade-up">
                  <div className="prose prose-invert max-w-none space-y-6">
                    <p className="font-body text-gray-400 text-sm">Last Modified Date: March 13, 2026</p>
                    <p className="font-body text-gray-300">These Terms of Service ("Terms") are entered into by and between you and Mind Relief Studios, LLC ("Mind Relief Studios, LLC"). The following terms and conditions govern your use of our suite of applications, including but not limited to Treatment Finder, Mental Health Care Guide, Threshold, and SignalWell (the "Apps"). By downloading, installing, accessing, or using the Apps, you agree to these Terms. If you do not agree, do not download or install the Apps. By using the Apps, or by clicking to accept or agree to the Terms when this option is made available to you, you accept and agree to be bound and abide by these Terms, and acknowledge that you have read and understood our Privacy Statement. If you do not want to agree to these Terms, you must not download, install, access, or use the Apps.</p>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">1. Nature of the Apps</h4>
                      <p className="font-body text-gray-400">The Apps are designed for educational and informational purposes only. The Apps provide a suite of options to obtain general, non-personalized information about mental health treatment approaches, information about your mood, and publicly available resources. The Apps do not provide medical advice, diagnosis, treatment, or clinical evaluation.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">2. No Medical Relationship</h4>
                      <p className="font-body text-gray-400 mb-2">Use of the Apps does not establish a physician–patient, therapist–client, or any other clinical relationship. The Apps:</p>
                      <ul className="font-body text-gray-400 space-y-1">
                        <li>• Do not provide medical advice</li>
                        <li>• Do not diagnose any condition</li>
                        <li>• Do not replace licensed medical or mental health care</li>
                      </ul>
                      <p className="font-body text-gray-400 mt-2">The Apps are not a substitute for professional medical care. You are solely responsible for seeking appropriate care from licensed professionals.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">3. User Responsibility</h4>
                      <p className="font-body text-gray-400 mb-2">The information presented on or through the Apps is made available solely for general information and education purposes. Mind Relief Studios, LLC does not warrant the accuracy, completeness, or usefulness of this information. Any reliance you place on such information is strictly at your own risk. Mind Relief Studios, LLC disclaims all liability and responsibility arising from any reliance placed on such materials by you and any other user of the Apps, or by anyone who may be informed of any of its contents.</p>
                      <p className="font-body text-gray-400 mb-2">You agree to use the Apps responsibly and acknowledge that:</p>
                      <ul className="font-body text-gray-400 space-y-1">
                        <li>• Any entries or inputs are self-reported and not verified</li>
                        <li>• Outputs are informational or visual only and do not constitute interpretation</li>
                        <li>• You are responsible for how you use any information provided</li>
                      </ul>
                      <p className="font-body text-gray-400 mt-2">You should not make medical decisions based solely on information from the Apps.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">4. Emergency Situations</h4>
                      <p className="font-body text-gray-400 mb-2">The Apps are not designed for crisis or emergency use. If you are experiencing a medical or mental health emergency, including thoughts of self-harm or harm to others, you must immediately contact:</p>
                      <ul className="font-body text-gray-400 space-y-1">
                        <li>• Emergency services (e.g., 911 in the United States)</li>
                        <li>• The 988 Suicide & Crisis Lifeline (U.S.)</li>
                        <li>• A qualified crisis hotline or healthcare provider</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">5. Free Trial and Purchase</h4>
                      <p className="font-body text-gray-400">While many of the Apps are available without charge, certain of the Apps may offer premium features available for a fee. The Apps may offer a 7-day free trial, which provides partial access to premium features. After the trial ends, continued access requires a one-time purchase. Payment is processed through your Apple ID account. The purchase is a one-time, non-recurring payment. There are no subscriptions or automatic renewals. All purchases are handled by Apple. Refund requests must be made through Apple and are subject to Apple's policies.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">6. Intellectual Property</h4>
                      <p className="font-body text-gray-400">The Apps and their entire contents, features, and functionality are owned by Mind Relief Studios, LLC, its licensors, or other providers of such material and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">7. Disclaimer of Warranties</h4>
                      <p className="font-body text-gray-400 mb-2">You understand that we cannot and do not guarantee or warrant that files available for downloading from the internet or the Apps will be free of viruses or other destructive code. You are responsible for implementing sufficient procedures and checkpoints to satisfy your particular requirements for anti-virus protection and accuracy of data input and output, and for maintaining a means external to our site for any reconstruction of any lost data.</p>
                      <p className="font-body text-gray-400 mb-2">TO THE FULLEST EXTENT PROVIDED BY LAW, WE WILL NOT BE LIABLE FOR ANY LOSS OR DAMAGE CAUSED BY A DISTRIBUTED DENIAL-OF-SERVICE ATTACK, VIRUSES, OR OTHER TECHNOLOGICALLY HARMFUL MATERIAL THAT MAY INFECT YOUR COMPUTER EQUIPMENT, COMPUTER PROGRAMS, DATA, OR OTHER PROPRIETARY MATERIAL DUE TO YOUR USE OF THE APPS OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE APPS, OR YOUR DOWNLOADING OF ANY MATERIAL POSTED ON IT, OR ON ANY WEBSITES LINKED TO IT.</p>
                      <p className="font-body text-gray-400 mb-2">YOUR USE OF THE APPS, ITS CONTENT, AND ANY SERVICES OR ITEMS OBTAINED THROUGH THE APPS IS AT YOUR OWN RISK. THE APPS, THEIR CONTENT, AND ANY SERVICES OR ITEMS OBTAINED THROUGH THE APPS ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. NEITHER MIND RELIEF STUDIOS NOR ANY PERSON ASSOCIATED WITH MIND RELIEF STUDIOS MAKES ANY WARRANTY OR REPRESENTATION WITH RESPECT TO THE COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY, OR AVAILABILITY OF THE APPS. WITHOUT LIMITING THE FOREGOING, NEITHER MIND RELIEF STUDIOS NOR ANYONE ASSOCIATED WITH MIND RELIEF STUDIOS REPRESENTS OR WARRANTS THAT THE APPS, THEIR CONTENT, OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE APPS WILL BE ACCURATE, RELIABLE, ERROR-FREE, OR UNINTERRUPTED, THAT DEFECTS WILL BE CORRECTED, THAT OUR SITE OR THE SERVER THAT MAKES THEM AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS, OR THAT THE APPS, ANY SERVICES, OR ITEMS OBTAINED THROUGH THE APPS WILL OTHERWISE MEET YOUR NEEDS OR EXPECTATIONS.</p>
                      <p className="font-body text-gray-400 mb-2">TO THE FULLEST EXTENT PROVIDED BY LAW, MIND RELIEF STUDIOS HEREBY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF MERCHANTABILITY, NON-INFRINGEMENT, AND FITNESS FOR PARTICULAR PURPOSE.</p>
                      <p className="font-body text-gray-400">THE FOREGOING DOES NOT AFFECT ANY WARRANTIES THAT CANNOT BE EXCLUDED OR LIMITED UNDER APPLICABLE LAW.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">7. Limitation of Liability</h4>
                      <p className="font-body text-gray-400 mb-2">TO THE FULLEST EXTENT PROVIDED BY LAW, IN NO EVENT WILL MIND RELIEF STUDIOS, ITS AFFILIATES, OR THEIR LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE APPS, ANY WEBSITES OR OTHER CONTENT LINKED TO THEM, ANY RELIANCE ON ANY INFORMATION PROVIDED THROUGH OR BY THE APPS, ANY DECISIONS MADE BASED ON THE APPS, ANY CONTENT ON THE APPS OR SUCH OTHER WEBSITES, INCLUDING ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO, PERSONAL INJURY, PAIN AND SUFFERING, EMOTIONAL DISTRESS, LOSS OF REVENUE, LOSS OF PROFITS, LOSS OF BUSINESS OR ANTICIPATED SAVINGS, LOSS OF USE, LOSS OF GOODWILL, LOSS OF DATA, AND WHETHER CAUSED BY TORT (INCLUDING NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE, EVEN IF FORESEEABLE.</p>
                      <p className="font-body text-gray-400">THE FOREGOING DOES NOT AFFECT ANY LIABILITY THAT CANNOT BE EXCLUDED OR LIMITED UNDER APPLICABLE LAW.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">8. Modifications</h4>
                      <p className="font-body text-gray-400">We reserve the right to update or modify the Apps or Terms at any time. Continued use of the Apps after changes are posted constitutes acceptance of the updated Terms. Mind Relief Studios, LLC may update the content of the Apps from time to time, but their content is not necessarily complete or up-to-date. Any of the material in the Apps may be out of date at any given time, and Mind Relief Studios, LLC is under no obligation to update such material.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">9. Governing Law</h4>
                      <p className="font-body text-gray-400">These Terms shall be governed by and construed in accordance with the laws of Illinois, without regard to conflict of law principles.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-display text-base font-bold text-white mb-2">10. Contact</h4>
                      <p className="font-body text-gray-400">For questions regarding these Terms, please contact: <a href="mailto:info@mindrelief.app" className="text-[var(--color-primary)] hover:underline">info@mindrelief.app</a></p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Medical Disclaimer */}
            <div>
              <button onClick={() => setActiveApp(activeApp === 'medical' ? null : 'medical')} className="w-full flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <span className="font-display text-xl font-bold text-gray-300">Medical Disclaimer</span>
                <svg className={`w-5 h-5 text-gray-400 transition-transform ${activeApp === 'medical' ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
              </button>
              {activeApp === 'medical' && (
                <div className="p-6 rounded-xl bg-white/5 animate-fade-up">
                  <div className="prose prose-invert max-w-none space-y-4">
                    <p className="font-body text-gray-400 text-sm">Last Modified Date: March 13, 2026</p>
                    <p className="font-body text-gray-300">The information and tools provided by the Mind Relief Studios, LLC ("Mind Relief Studios, LLC") suite of applications (the "Apps") are for general educational and informational purposes only. The Apps do not provide medical advice, diagnosis, or treatment and do not constitute medical, psychological, psychiatric, or therapeutic advice.</p>
                    <p className="font-body text-gray-400">The Apps do not interpret user data, assign diagnoses, or make treatment recommendations.</p>
                    <p className="font-body text-gray-400">Use of the Apps does not create a physician–patient or clinician–patient relationship between you and Mind Relief or any of its affiliates or subsidiaries.</p>
                    <p className="font-body text-gray-300">Do not rely on the Apps as a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of a qualified healthcare provider with any questions you may have regarding a medical or mental health condition. Do not disregard, delay, or avoid seeking professional mental health advice because of information or guidance provided through the Apps.</p>
                    <p className="font-body text-gray-300">If you are experiencing distress, worsening symptoms, or thoughts of harming yourself or others, seek immediate assistance by contacting emergency services, a crisis hotline, or a qualified healthcare professional.</p>
                    <p className="font-body text-gray-400">While reasonable efforts are made to provide accurate and up-to-date information, no guarantees are made regarding completeness, reliability, or applicability to your individual situation.</p>
                    <p className="font-body text-gray-400 italic">By using the Apps, you acknowledge and agree that all use is at your own discretion and risk.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-8 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="relative w-8 h-8">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--color-primary)] via-[var(--color-calm)] to-[var(--color-deep)]" />
                <div className="absolute inset-0.5 rounded-full bg-white flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-[var(--color-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4" strokeLinecap="round" /></svg>
                </div>
              </div>
              <span className="font-display text-lg font-bold text-gray-900">Mind Relief Studios, LLC</span>
            </div>
            <div className="text-center md:text-right">
              <p className="font-body text-sm text-gray-400">© 2026 Mind Relief Studios, LLC. All rights reserved.</p>
              <p className="font-body text-xs text-gray-300 mt-2">Mind Relief Studios, LLC — Founded by a physician.</p>
              <p className="font-body text-xs text-gray-300 mt-1">MoodQuad is developed and published by Mind Relief Studios, LLC</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Helper Components
const PrincipleCard: React.FC<{ number: string; title: string; description: string; color: string }> = ({ number, title, description, color }) => (
  <div className="group p-8 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all">
    <span className="font-body text-5xl font-bold mb-4 block" style={{ color, opacity: 0.3 }}>{number}</span>
    <h3 className="font-display text-xl font-bold text-white mb-3">{title}</h3>
    <p className="font-body text-gray-400">{description}</p>
  </div>
);

// MoodQuad Card
const MoodQuadCard: React.FC<{ activeApp: string | null; setActiveApp: (app: string | null) => void }> = ({ activeApp, setActiveApp }) => {
  const expanded = activeApp === 'moodquad';
  const toggleExpanded = () => setActiveApp(expanded ? null : 'moodquad');
  const [position, setPosition] = useState({ x: 0.35, y: 0.35 });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const quadrantLabels: Record<string, { name: string; desc: string; color: string }> = {
    energized: { name: 'Energized', desc: 'High energy and positive emotions. Great time for creative work or connecting with others.', color: '#33A0D9' },
    restless: { name: 'Restless', desc: 'High energy but negative valence. You may feel anxious, agitated, or on edge.', color: '#E6804D' },
    lowEnergy: { name: 'Low Energy', desc: 'Low energy and negative valence. You may feel drained, sad, or withdrawn.', color: '#9980B3' },
    calm: { name: 'Calm', desc: 'Low energy and positive emotions. A peaceful, grounded state ideal for reflection.', color: '#66BF99' },
  };
  const currentQuadrant = position.x >= 0 && position.y >= 0 ? 'energized' : position.x < 0 && position.y >= 0 ? 'restless' : position.x < 0 && position.y < 0 ? 'lowEnergy' : 'calm';
  const currentQ = quadrantLabels[currentQuadrant];

  const handleMove = (clientX: number, clientY: number) => {
    if (!containerRef.current || !isDragging) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(-1, Math.min(1, ((clientX - rect.left) / rect.width) * 2 - 1));
    const y = Math.max(-1, Math.min(1, -(((clientY - rect.top) / rect.height) * 2 - 1)));
    setPosition({ x, y });
  };

  const keyFeatures = [
    { name: '4-Quadrant Emotion Mapping', desc: 'Using the validated Russell Circumplex Model', icon: '📊' },
    { name: 'Automatic Mood Detection', desc: 'Via HealthKit integration (HR, HRV, sleep)', icon: '❤️' },
    { name: 'Smart Predictions', desc: "Ghost dot shows where you're likely heading", icon: '✨' },
    { name: 'Advanced Insights', desc: 'Trend forecasting & anomaly detection', icon: '🔍' },
  ];

  const howItWorks = [
    { title: 'Automatic Detection', desc: 'MoodQuad calculates your mood from heart rate, HRV, sleep duration, and respiratory rate through HealthKit.' },
    { title: '4-Quadrant Mapping', desc: 'Your mood is mapped to one of four quadrants: Energized, Calm, Restless, or Low Energy.' },
    { title: 'Smart Predictions', desc: 'The app learns your patterns and predicts your mood based on time, day, and activity patterns.' },
    { title: 'Actionable Insights', desc: 'Get personalized suggestions like "When you log work at 2pm, your HR averages 15 bpm higher."' },
  ];

  return (
    <div className="app-card group relative p-6 md:p-8 rounded-3xl bg-gradient-to-br from-[#F5F5F0] to-[#EEEDE8] border border-[var(--color-primary)]/20 overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <img src="/icon-moodquad.png" alt="MoodQuad" className="w-14 h-14 rounded-2xl" />
          <div>
            <div className="flex items-center gap-3">
              <h3 className="font-display text-2xl font-bold text-gray-900 tracking-tight">MoodQuad</h3>
              <span className="px-2 py-0.5 rounded bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-body text-[10px] font-bold uppercase tracking-wider">Flagship</span>
            </div>
            <p className="font-body text-gray-500 text-sm">4-Quadrant Emotion Mapping</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Left Column */}
        <div>
          <span className="font-body text-sm font-bold text-[var(--color-primary)] uppercase tracking-[0.15em] mb-3 block">Launching Summer 2026</span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-gray-900 tracking-tight leading-[1.1] mb-6">Track Your<br />Mood in <span className="text-[var(--color-primary)]">4</span><br /><span className="text-transparent bg-clip-text animate-gradient italic pr-2" style={{ backgroundImage: 'linear-gradient(135deg, var(--color-primary), var(--color-calm), var(--color-deep), var(--color-warm), var(--color-primary))', backgroundSize: '300% 300%' }}>Dimensions</span></h2>

          <p className="font-body text-gray-600 leading-relaxed mb-4">The only mood tracker built on the validated Russell Circumplex Model. Instead of asking "<span className="font-semibold text-gray-800">How</span> do you feel?" We show you <span className="font-semibold text-gray-800">why</span> you feel that way.</p>

          {/* Quote */}
          <div className="flex items-start gap-3 mb-4">
            <div className="w-1 h-full bg-gray-300 rounded-full flex-shrink-0" />
            <p className="font-body text-gray-500 text-sm italic">Built on the clinical observation that mood is not a feeling — it's a pattern. And patterns need data.</p>
          </div>

          <p className="font-body text-gray-400 text-sm mb-4 flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
            Try it — drag the dot on the model to explore your emotional landscape
          </p>

          {/* Show More/Less Toggle */}
          <button
            onClick={toggleExpanded}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-primary)]/30 text-[var(--color-primary)] font-body text-sm font-semibold hover:bg-[var(--color-primary)]/5 transition-colors mb-6"
          >
            {expanded ? 'Show Less' : 'Show More'}
            <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
          </button>

          {/* Expandable Content */}
          {expanded && (
            <div className="space-y-8">
              {/* Key Features */}
              <div>
                <h4 className="font-display text-xl font-bold text-gray-900 mb-4">Key Features</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {keyFeatures.map((f, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-sm flex-shrink-0">{f.icon}</div>
                      <div>
                        <p className="font-body text-sm font-semibold text-gray-900">{f.name}</p>
                        <p className="font-body text-xs text-gray-500">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* How It Works */}
              <div>
                <h4 className="font-display text-xl font-bold text-gray-900 mb-4">How It Works</h4>
                <div className="space-y-4">
                  {howItWorks.map((step, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-[var(--color-primary)] text-white flex items-center justify-center font-body text-sm font-bold flex-shrink-0">{i + 1}</div>
                      <div>
                        <p className="font-body text-sm font-semibold text-gray-900">{step.title}</p>
                        <p className="font-body text-sm text-gray-500">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Simple Pricing */}
              <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
                <h4 className="font-display text-lg font-bold text-gray-900 mb-1">Simple Pricing</h4>
                <p className="font-body text-gray-500 text-sm mb-3">Start free. Upgrade when you're ready.</p>
                <div className="space-y-2">
                  <p className="font-body text-gray-700"><span className="font-display text-xl font-bold">Free</span> <span className="text-gray-400">— Basic tracking & insights</span></p>
                  <p className="font-body text-gray-700"><span className="font-display text-xl font-bold text-[var(--color-primary)]">$3.99</span> <span className="text-gray-400">one-time — Full features</span></p>
                </div>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="flex items-center gap-4 mt-6">
            <a href="https://apps.apple.com/us/app/moodquad/id6757625815" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
              <img src="/app-store-badge.svg" alt="Download on the App Store" className="h-10 w-auto" />
            </a>
          </div>
        </div>

        {/* Right Column - Dark Quadrant Model */}
        <div className="flex flex-col items-center justify-start">
          <p className="font-body text-xs font-semibold text-gray-600 uppercase tracking-widest mb-2">High Energy</p>
          <div ref={containerRef} className="relative w-full aspect-square max-w-sm cursor-crosshair select-none" onMouseDown={() => setIsDragging(true)} onMouseUp={() => setIsDragging(false)} onMouseLeave={() => setIsDragging(false)} onMouseMove={(e) => handleMove(e.clientX, e.clientY)} onTouchStart={() => setIsDragging(true)} onTouchEnd={() => setIsDragging(false)} onTouchMove={(e) => handleMove(e.touches[0].clientX, e.touches[0].clientY)}>
            {/* Dark background circle */}
            <div className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(circle at center, #2a2a3e 0%, #1a1a2e 60%, #111122 100%)' }} />
            {/* Subtle color quadrant glow */}
            <div className="absolute inset-0 rounded-full overflow-hidden">
              <div className="absolute top-0 right-0 w-1/2 h-1/2" style={{ background: 'radial-gradient(circle at 70% 70%, rgba(51,160,217,0.25) 0%, transparent 70%)' }} />
              <div className="absolute top-0 left-0 w-1/2 h-1/2" style={{ background: 'radial-gradient(circle at 30% 70%, rgba(230,128,77,0.2) 0%, transparent 70%)' }} />
              <div className="absolute bottom-0 left-0 w-1/2 h-1/2" style={{ background: 'radial-gradient(circle at 30% 30%, rgba(153,128,179,0.2) 0%, transparent 70%)' }} />
              <div className="absolute bottom-0 right-0 w-1/2 h-1/2" style={{ background: 'radial-gradient(circle at 70% 30%, rgba(102,191,153,0.2) 0%, transparent 70%)' }} />
            </div>
            {/* Inner ring */}
            <div className="absolute inset-6 rounded-full border border-white/10" />
            <div className="absolute inset-12 rounded-full border border-white/5" />
            {/* Crosshairs */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-px h-full bg-white/10" />
              <div className="absolute w-full h-px bg-white/10" />
            </div>
            {/* Quadrant labels */}
            <div className="absolute font-body text-xs font-semibold" style={{ top: '12%', left: '18%', color: '#E6804D' }}>
              <span>Restless</span>
              <span className="block text-[9px] font-normal text-white/70 mt-0.5">HIGH ENERGY · NEGATIVE</span>
            </div>
            <div className="absolute font-body text-xs font-semibold" style={{ top: '12%', right: '12%', color: '#33A0D9', textAlign: 'right' }}>
              <span>Energized</span>
              <span className="block text-[9px] font-normal text-white/70 mt-0.5">HIGH ENERGY · POSITIVE</span>
            </div>
            <div className="absolute font-body text-xs font-semibold" style={{ bottom: '14%', left: '18%', color: '#9980B3' }}>
              <span>Low Energy</span>
              <span className="block text-[9px] font-normal text-white/70 mt-0.5">LOW ENERGY · NEGATIVE</span>
            </div>
            <div className="absolute font-body text-xs font-semibold" style={{ bottom: '14%', right: '12%', color: '#66BF99', textAlign: 'right' }}>
              <span>Calm</span>
              <span className="block text-[9px] font-normal text-white/70 mt-0.5">LOW ENERGY · POSITIVE</span>
            </div>
            {/* Axis labels on edges */}
            <div className="absolute font-body text-[10px] font-bold text-white/80 uppercase tracking-widest" style={{ left: '-2px', top: '50%', transform: 'translateY(-50%) rotate(-90deg)' }}>Negative</div>
            <div className="absolute font-body text-[10px] font-bold text-white/80 uppercase tracking-widest" style={{ right: '-2px', top: '50%', transform: 'translateY(-50%) rotate(90deg)' }}>Positive</div>
            {/* Glowing dot */}
            <div className="absolute w-5 h-5 rounded-full transform -translate-x-1/2 -translate-y-1/2 transition-all duration-150 cursor-grab active:cursor-grabbing" style={{ left: `${(position.x + 1) / 2 * 100}%`, top: `${(-position.y + 1) / 2 * 100}%`, backgroundColor: currentQ.color, boxShadow: `0 0 15px ${currentQ.color}80, 0 0 30px ${currentQ.color}40` }} />
          </div>
          <p className="font-body text-xs font-semibold text-gray-600 uppercase tracking-widest mt-2">Low Energy</p>

          {/* Quadrant Description Card */}
          <div className="mt-4 w-full max-w-sm p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: currentQ.color }} />
                <span className="font-display text-lg font-bold text-gray-900">{currentQ.name}</span>
              </div>
              <span className="text-lg">✨</span>
            </div>
            <p className="font-body text-sm text-gray-500 leading-relaxed">{currentQ.desc}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Care Guide Card
const CareGuideCard: React.FC<{ activeApp: string | null; setActiveApp: (app: string | null) => void }> = ({ activeApp, setActiveApp }) => {
  const expanded = activeApp === 'careguide';
  const toggleExpanded = () => setActiveApp(expanded ? null : 'careguide');
  
  const treatmentCategories = [
    { name: 'Talk Therapies', count: 5, icon: '💬', bg: '#6B7280' },
    { name: 'Daily Medications', count: 5, icon: '💊', bg: '#D97706' },
    { name: 'Rapid-Acting', count: 3, icon: '⚡', bg: '#9CA3AF' },
    { name: 'Neuromodulation', count: 3, icon: '🧠', bg: '#EC4899' },
    { name: 'Autonomic', count: 3, icon: '💕', bg: '#F9A8D4' },
    { name: 'Lifestyle & Skills', count: 5, icon: '🌱', bg: '#6B7280' },
  ];
  
  const conditions = ['Depression', 'Anxiety', 'PTSD', 'Chronic Pain', 'ADHD', 'Sleep', 'Bipolar', 'OCD', 'Eating Disorders', 'Substance Use'];
  
  return (
    <div className="app-card group relative p-6 md:p-8 rounded-3xl bg-gradient-to-br from-[#FFF7ED] to-[#FFFBF5] border border-[var(--color-guide)]/20 overflow-hidden self-start">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <img src="/icon-guide.png" alt="Mental Health Care Guide" className="w-14 h-14 rounded-2xl" />
          <div>
            <h3 className="font-display text-2xl font-bold text-gray-900 tracking-tight">Mental Health Care Guide</h3>
            <p className="font-body text-gray-500 text-sm">Explore your options with clarity and calm</p>
          </div>
        </div>
        <a href="https://apps.apple.com/us/app/6758245315" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
          <img src="/app-store-badge.svg" alt="Download on the App Store" className="h-10 w-auto" />
        </a>
      </div>
      
      {/* Our Approach Box */}
      <div className="p-5 rounded-2xl bg-white border border-gray-100 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[var(--color-guide)]/10 flex items-center justify-center">
            <svg className="w-4 h-4 text-[var(--color-guide)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
          </div>
          <h4 className="font-display text-lg font-bold text-gray-900">Our Approach</h4>
        </div>
        <p className="font-body text-gray-600 text-sm leading-relaxed mb-3">This app is designed to help you better understand mental health, not to diagnose or treat it. Built for clarity with content designed to be calm, simple, and easy to navigate.</p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-[var(--color-guide)]" viewBox="0 0 24 24" fill="currentColor"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span className="font-body text-sm text-gray-600">Education</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-[var(--color-guide)]" viewBox="0 0 24 24" fill="currentColor"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span className="font-body text-sm text-gray-600">Awareness</span>
          </div>
        </div>
      </div>
      
      {/* Quote */}
      <div className="flex items-start gap-3 mb-6">
        <div className="w-1 h-full bg-gray-200 rounded-full flex-shrink-0" />
        <p className="font-body text-gray-500 text-sm italic">Built because most patients leave appointments with more confusion than clarity.</p>
      </div>
      
      {/* Show More/Less Toggle */}
      <button 
        onClick={toggleExpanded} 
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-guide)]/30 text-[var(--color-guide)] font-body text-sm font-semibold hover:bg-[var(--color-guide)]/5 transition-colors mb-6"
      >
        {expanded ? 'Show Less' : 'Show More'}
        <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
      </button>
      
      {/* Expandable Content */}
      {expanded && (
        <div className="space-y-8">
          {/* Treatment Categories */}
          <div>
            <h4 className="font-display text-xl font-bold text-gray-900 mb-2">Treatment Categories</h4>
            <p className="font-body text-gray-500 text-sm mb-4">Understand your options by type</p>
            <div className="grid grid-cols-2 gap-3">
              {treatmentCategories.map((cat, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg" style={{ backgroundColor: `${cat.bg}20` }}>{cat.icon}</div>
                  <div>
                    <p className="font-body text-sm font-semibold text-gray-900">{cat.name}</p>
                    <p className="font-body text-xs text-gray-400">{cat.count} treatments</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Find a Provider */}
          <div>
            <h4 className="font-display text-xl font-bold text-gray-900 mb-2">Find a Provider</h4>
            <p className="font-body text-gray-500 text-sm mb-4">Search for professionals in your area</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {conditions.map((condition, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-white border border-gray-200 font-body text-xs text-gray-600">{condition}</span>
              ))}
            </div>
          </div>
          
          {/* Better Conversations Box */}
          <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
            <p className="font-body text-gray-500 text-sm mb-1">We provide education and vocabulary to have</p>
            <p className="font-body text-gray-900 font-semibold">better conversations with your healthcare providers.</p>
          </div>
        </div>
      )}
      
      {/* Price and CTA */}
      <div className="mt-6 pt-6 border-t border-[var(--color-guide)]/10">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="font-display text-2xl font-bold text-[var(--color-guide)]">Free</span>
          <span className="font-body text-gray-500">— Education for everyone</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="https://apps.apple.com/us/app/6758245315" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--color-guide)] text-white rounded-full font-body text-sm font-semibold hover:bg-[var(--color-guide)]/90 transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            Get Free on App Store
          </a>
        </div>
      </div>
    </div>
  );
};

// TreatmentFinder Card
const TreatmentFinderCard: React.FC<{ activeApp: string | null; setActiveApp: (app: string | null) => void }> = ({ activeApp, setActiveApp }) => {
  const expanded = activeApp === 'treatmentfinder';
  const toggleExpanded = () => setActiveApp(expanded ? null : 'treatmentfinder');
  
  const categories = [
    { name: 'Medications', desc: 'Daily and as-needed options', icon: '💊', bg: '#F59E0B' },
    { name: 'Specialized Procedures', desc: 'Clinical interventions', icon: '🏥', bg: '#EC4899' },
    { name: 'Therapeutic Interventions', desc: 'Talk therapy and beyond', icon: '💬', bg: '#6B7280' },
    { name: 'Supportive & Adjunctive', desc: 'Complementary approaches', icon: '💛', bg: '#F59E0B' },
    { name: 'Lifestyle Optimization', desc: 'Daily habits that matter', icon: '🔧', bg: '#10B981' },
  ];
  
  const features = [
    'Evidence level (High / Moderate / Emerging)',
    'Typical cost range',
    'Conditions it\'s indicated for',
    'Potential warnings and considerations',
    'Links to find providers near you',
  ];
  
  return (
    <div className="app-card group relative p-6 md:p-8 rounded-3xl bg-gradient-to-br from-[#F0F5F3] to-[#E8F0EC] border border-[#4A9B7F]/20 overflow-hidden self-start">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <img src="/icon-treatment.png" alt="TreatmentFinder" className="w-14 h-14 rounded-2xl" />
          <div>
            <h3 className="font-display text-2xl font-bold text-gray-900 tracking-tight">TreatmentFinder</h3>
            <p className="font-body text-gray-500 text-sm">Explore mental health treatments that fit your needs</p>
          </div>
        </div>
        <a href="https://apps.apple.com/us/app/6759846067" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
          <img src="/app-store-badge.svg" alt="Download on the App Store" className="h-10 w-auto" />
        </a>
      </div>
      
      {/* How It Works Box */}
      <div className="p-5 rounded-2xl bg-white border border-gray-100 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[#4A9B7F]/10 flex items-center justify-center">
            <svg className="w-4 h-4 text-[#4A9B7F]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </div>
          <h4 className="font-display text-lg font-bold text-gray-900">How It Works</h4>
        </div>
        <p className="font-body text-gray-600 text-sm leading-relaxed mb-3">Answer three simple questions about what you're experiencing, how long, and how severely. Tell us what you've tried. We organize the options — you take the results to your clinician.</p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-[#4A9B7F]" viewBox="0 0 24 24" fill="currentColor"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span className="font-body text-sm text-gray-600">38 Treatments</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-[#4A9B7F]" viewBox="0 0 24 24" fill="currentColor"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span className="font-body text-sm text-gray-600">5 Categories</span>
          </div>
        </div>
      </div>
      
      {/* Quote */}
      <div className="flex items-start gap-3 mb-6">
        <div className="w-1 h-full bg-gray-300 rounded-full flex-shrink-0" />
        <p className="font-body text-gray-500 text-sm italic">Built from clinical frustration watching patients arrive at appointments unable to articulate what they'd tried and why it hadn't worked.</p>
      </div>
      
      {/* Show More/Less Toggle */}
      <button 
        onClick={toggleExpanded} 
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#4A9B7F]/30 text-[#4A9B7F] font-body text-sm font-semibold hover:bg-[#4A9B7F]/5 transition-colors mb-6"
      >
        {expanded ? 'Show Less' : 'Show More'}
        <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
      </button>
      
      {/* Expandable Content */}
      {expanded && (
        <div className="space-y-8">
          {/* 38 Treatments */}
          <div>
            <h4 className="font-display text-xl font-bold text-gray-900 mb-4">38 Treatments Across 5 Categories</h4>
            <div className="space-y-3">
              {categories.map((cat, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/70 border border-gray-100">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg" style={{ backgroundColor: `${cat.bg}20` }}>{cat.icon}</div>
                  <div>
                    <p className="font-body text-sm font-semibold text-gray-900">{cat.name}</p>
                    <p className="font-body text-xs text-gray-400">{cat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* For Every Treatment */}
          <div>
            <h4 className="font-display text-xl font-bold text-gray-900 mb-3">For Every Treatment You'll Find</h4>
            <ul className="space-y-2">
              {features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#4A9B7F]" />
                  <span className="font-body text-sm text-gray-600">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Track Your History */}
          <div>
            <h4 className="font-display text-xl font-bold text-gray-900 mb-2">Track Your History</h4>
            <p className="font-body text-gray-600 text-sm leading-relaxed">Log treatments you've tried and why they were stopped — ineffective, side effects, or not completed. This helps filter results and gives your clinician a clearer picture of your journey.</p>
          </div>
          
          {/* Browse Box */}
          <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
            <p className="font-body text-gray-500 text-sm mb-1">Not ready for the wizard?</p>
            <p className="font-body text-gray-900 font-semibold">Browse all 38 treatments organized by category anytime, no questions required.</p>
          </div>
        </div>
      )}
      
      {/* Price and CTA */}
      <div className="mt-6 pt-6 border-t border-[#4A9B7F]/10">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="font-display text-2xl font-bold text-[#4A9B7F]">$4.99</span>
          <span className="font-body text-gray-500">one-time — Your treatment journey, organized</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="https://apps.apple.com/us/app/6759846067" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#4A9B7F] text-white rounded-full font-body text-sm font-semibold hover:bg-[#4A9B7F]/90 transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            Get on App Store
          </a>
        </div>
      </div>
    </div>
  );
};

// SignalWell Card
const SignalWellCard: React.FC<{ activeApp: string | null; setActiveApp: (app: string | null) => void }> = ({ activeApp, setActiveApp }) => {
  const expanded = activeApp === 'signalwell';
  const toggleExpanded = () => setActiveApp(expanded ? null : 'signalwell');
  
  const features = [
    { name: 'Voice Check-ins', desc: 'Speak naturally for 20-30 seconds', icon: '🎙️' },
    { name: '4-Quadrant Mapping', desc: 'Pleasant/Unpleasant × Activated/Calm', icon: '📊' },
    { name: 'Live Energy Analysis', desc: 'Vocal stability & signal quality tracking', icon: '⚡' },
    { name: 'Rhythm Metrics', desc: 'Speech flow, pause ratio, variability', icon: '🎵' },
  ];
  
  const steps = [
    'Start a check-in and speak naturally for about 20-30 seconds',
    'SignalWell analyzes pleasantness, energy, and vocal stability',
    'View your position on the mood quadrant and track patterns over time',
  ];
  
  const tips = [
    'Choose a quieter place if possible',
    'Hold the phone 6-12 inches away',
    'Speak in your normal voice',
  ];
  
  return (
    <div className="app-card group relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1a1a2e] to-[#2d2d44] border border-[var(--color-signal)]/20">
      <div className="p-8 md:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column */}
          <div>
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <img src="/icon-signal.png" alt="SignalWell" className="w-14 h-14 rounded-2xl" />
                <div>
                  <h3 className="font-display text-2xl font-bold text-white tracking-tight">SignalWell</h3>
                  <p className="font-body text-[#F59E0B] text-sm">Your Voice, Your Mood</p>
                </div>
              </div>
              <span className="text-[var(--color-signal)] font-body text-xs font-semibold uppercase tracking-wide">August 2026</span>
            </div>
            
            {/* Description */}
            <p className="font-body text-xl text-gray-300 leading-relaxed mb-4">Your voice carries patterns — SignalWell helps you see them. Record brief check-ins and track changes in how you express yourself over time.</p>
            <p className="font-body text-gray-400 mb-6">No labels. No diagnosis. Just insight.</p>
            
            {/* Quote */}
            <div className="flex items-start gap-3 mb-6">
              <div className="w-1 h-full bg-gray-600 rounded-full flex-shrink-0" />
              <p className="font-body text-gray-400 text-sm italic">Built on the clinical insight that the voice carries emotional information that neither patient nor provider consciously registers.</p>
            </div>
            
            {/* Show More/Less Toggle */}
            <button 
              onClick={toggleExpanded} 
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-signal)]/30 text-[var(--color-signal)] font-body text-sm font-semibold hover:bg-[var(--color-signal)]/5 transition-colors mb-6"
            >
              {expanded ? 'Show Less' : 'Show More'}
              <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
            </button>
            
            {/* Expandable Content */}
            {expanded && (
              <div className="space-y-8">
                {/* Key Features */}
                <div>
                  <h4 className="font-display text-lg font-bold text-white mb-4">Key Features</h4>
                  <div className="space-y-3">
                    {features.map((f, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-lg">{f.icon}</div>
                        <div>
                          <p className="font-body text-sm font-semibold text-white">{f.name}</p>
                          <p className="font-body text-xs text-gray-400">{f.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* How It Works */}
                <div>
                  <h4 className="font-display text-lg font-bold text-white mb-4">How It Works</h4>
                  <div className="space-y-3">
                    {steps.map((step, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#F59E0B] flex items-center justify-center flex-shrink-0">
                          <span className="font-body text-xs font-bold text-white">{i + 1}</span>
                        </div>
                        <p className="font-body text-sm text-gray-300">{step}</p>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Tips Box */}
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <p className="font-body text-xs font-bold text-[#F59E0B] uppercase tracking-wide mb-3">For Clearer Signals</p>
                  <div className="space-y-2">
                    {tips.map((tip, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-[#F59E0B]">✦</span>
                        <span className="font-body text-sm text-gray-300">{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* CTA */}
            <div className="flex items-center gap-4 mt-6">
              <a href="#" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#F59E0B] text-white rounded-full font-body text-sm font-semibold hover:bg-[#F59E0B]/90 transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                Coming August 2026
              </a>
            </div>
          </div>
          
          {/* Right Column - Quadrant Visualization */}
          <div className="flex flex-col items-center justify-center">
            <div className="relative w-64 h-64">
              {/* Quadrant Circle */}
              <div className="absolute inset-0 rounded-full" style={{ background: 'conic-gradient(from 45deg, #4A9B7F, #6B7280, #4A9B7F, #6B7280, #4A9B7F)' }} />
              <div className="absolute inset-2 rounded-full bg-[#1a1a2e]" />
              <div className="absolute inset-4 rounded-full" style={{ background: 'conic-gradient(from 45deg, rgba(74,155,127,0.3), rgba(107,114,128,0.3), rgba(74,155,127,0.3), rgba(107,114,128,0.3), rgba(74,155,127,0.3))' }} />
              <div className="absolute inset-8 rounded-full bg-[#1a1a2e]/80" />
              
              {/* Labels */}
              <div className="absolute top-4 left-1/4 text-xs font-body text-white/90 font-medium">Unpleasant<br/>Activated</div>
              <div className="absolute top-4 right-4 text-xs font-body text-white/90 font-medium text-right">Pleasant<br/>Activated</div>
              <div className="absolute bottom-4 left-4 text-xs font-body text-white/90 font-medium">Unpleasant<br/>Calm</div>
              <div className="absolute bottom-4 right-4 text-xs font-body text-white/90 font-medium text-right">Pleasant<br/>Calm</div>
              
              {/* Data Point */}
              <div className="absolute top-8 right-1/3 w-3 h-3 rounded-full bg-[var(--color-signal)]" />
            </div>
            
            {/* Metrics */}
            <div className="mt-8 space-y-2 w-full max-w-xs">
              <div className="flex justify-between">
                <span className="font-body text-sm text-[var(--color-signal)]">Pleasantness</span>
                <span className="font-body text-sm text-white">0.00</span>
              </div>
              <div className="flex justify-between">
                <span className="font-body text-sm text-[#F59E0B]">Energy</span>
                <span className="font-body text-sm text-white">0.99</span>
              </div>
              <div className="flex justify-between">
                <span className="font-body text-sm text-[#4A9B7F]">Vocal Stability</span>
                <span className="font-body text-sm text-white">0.06</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Veterans Card
const VeteransCard: React.FC<{ activeApp: string | null; setActiveApp: (app: string | null) => void }> = ({ activeApp, setActiveApp }) => {
  const expanded = activeApp === 'veterans';
  const toggleExpanded = () => setActiveApp(expanded ? null : 'veterans');
  
  const coreTopics = [
    'Cognitive Processing Therapy (CPT)',
    'Depression & Loss of Motivation',
    'PTSD & Trauma-Related Symptoms',
    'Sleep & Nightmares',
    'Chronic Pain & Mental Health',
    'Neuromodulation: Brain & Nervous System Treatments',
    'Medications Overview',
    'Compare Treatment Options',
    'Support for Families & Caregivers',
  ];
  
  const moreOptions = [
    { icon: '📈', text: 'Optimization & Adjunct Approaches' },
    { icon: '🍄', text: 'Psychedelic-Assisted Therapy (Research & Safety)' },
  ];
  
  const sideTopics = [
    'PTSD & Trauma',
    'Depression',
    'Sleep & Nightmares',
    'Chronic Pain',
    'TBI Support',
    'Family Resources',
  ];
  
  return (
    <div className="app-card group relative p-6 md:p-8 rounded-3xl bg-gradient-to-br from-[#F5F5F0] to-[#EEEDE8] border border-[var(--color-veterans)]/20 overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <img src="/icon-veterans.png" alt="Service and Recovery" className="w-14 h-14 rounded-2xl" />
          <div>
            <h3 className="font-display text-2xl font-bold text-gray-900 tracking-tight">Mind Relief: Service and Recovery</h3>
            <p className="font-body text-gray-500 text-sm">For Veterans, Families & Supporters</p>
          </div>
        </div>
        <a href="https://apps.apple.com/us/app/6759178814" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
          <img src="/app-store-badge.svg" alt="Download on the App Store" className="h-10 w-auto" />
        </a>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content - 2 columns */}
        <div className="lg:col-span-2">
          {/* Description */}
          <p className="font-body text-xl text-gray-700 leading-relaxed mb-4">A calm, educational guide to mental health treatment options for veterans, families, and supporters.</p>
          <p className="font-body text-gray-400 text-sm italic mb-6">This app does not provide diagnosis, therapy, or medical advice.</p>
          
          {/* Quote */}
          <div className="flex items-start gap-3 mb-6">
            <div className="w-1 h-full bg-gray-300 rounded-full flex-shrink-0" />
            <p className="font-body text-gray-500 text-sm italic">Built with deep respect for a population that has given everything and often receives the least support navigating mental health care.</p>
          </div>
          
          {/* Show More/Less Toggle */}
          <button 
            onClick={toggleExpanded} 
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-veterans)]/30 text-[var(--color-veterans)] font-body text-sm font-semibold hover:bg-[var(--color-veterans)]/5 transition-colors mb-6"
          >
            {expanded ? 'Show Less' : 'Show More'}
            <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
          </button>
          
          {/* Expandable Content */}
          {expanded && (
            <div className="space-y-8">
              {/* Core Treatment Topics */}
              <div>
                <h4 className="font-display text-xl font-bold text-gray-900 mb-4">Core Treatment Topics</h4>
                <ul className="space-y-2">
                  {coreTopics.map((topic, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[var(--color-veterans)]" />
                      <span className="font-body text-sm text-gray-600">{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* More Options */}
              <div>
                <h4 className="font-display text-lg font-bold text-gray-900 mb-3">More Options</h4>
                <div className="space-y-2">
                  {moreOptions.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span>{opt.icon}</span>
                      <span className="font-body text-sm text-gray-600">{opt.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Help Box */}
              <div className="p-5 rounded-2xl bg-white border border-gray-100">
                <p className="font-body text-gray-500 text-sm mb-1">You're in control.</p>
                <p className="font-body text-gray-900 font-semibold mb-1">Get Help is always one tap away.</p>
                <p className="font-body text-gray-500 text-sm">Treatment overviews, pathways, and resources—all in one place.</p>
              </div>
            </div>
          )}
          
          {/* Price and CTA */}
          <div className="mt-6 pt-6 border-t border-[var(--color-veterans)]/10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="font-display text-2xl font-bold text-[var(--color-veterans)]">Free</span>
              <span className="font-body text-gray-500">— Always free for those who served - coming June 2026</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="#contact" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--color-veterans)] text-white rounded-full font-body text-sm font-semibold hover:bg-[var(--color-veterans)]/90 transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                Notify Me
              </a>
            </div>
          </div>
        </div>
        
        {/* Sidebar - Core Topics */}
        <div className="lg:col-span-1">
          <div className="p-5 rounded-2xl bg-white border border-gray-100 sticky top-24">
            <h4 className="font-display text-lg font-bold text-gray-900 mb-4">Core Topics</h4>
            <ul className="space-y-3">
              {sideTopics.map((topic, i) => (
                <li key={i} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[var(--color-veterans)]" />
                  <span className="font-body text-sm text-gray-600">{topic}</span>
                </li>
              ))}
            </ul>
            <p className="font-body text-xs text-gray-400 mt-4 italic">Get Help is always one tap away.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Threshold Card
const ThresholdCard: React.FC<{ activeApp: string | null; setActiveApp: (app: string | null) => void }> = ({ activeApp, setActiveApp }) => {
  const expanded = activeApp === 'threshold';
  const toggleExpanded = () => setActiveApp(expanded ? null : 'threshold');
  
  const features = [
    { name: 'Structured Check-ins', desc: 'Captures your mood and voice at every session milestone throughout your treatment arc', icon: '📍' },
    { name: 'Visual Journey Map', desc: 'Reveals your emotional transformation through a private timeline only you can see', icon: '🗺️' },
    { name: 'Voice & Mood Tracking', desc: 'Record how you feel before, during, and after each session across your entire journey', icon: '🎙️' },
    { name: 'Completely Private', desc: 'No account. No cloud. No subscription. Your healing stays yours.', icon: '🔒' },
  ];
  
  const protocols = ['Ketamine', 'Psilocybin', 'MDMA'];
  
  return (
    <div className="app-card group relative p-6 md:p-8 rounded-3xl bg-gradient-to-br from-[#F5F5F0] to-[#EEEDE8] border border-[var(--color-threshold)]/20 overflow-hidden self-start">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <img src="/icon-threshold.png" alt="Threshold" className="w-14 h-14 rounded-2xl" />
          <div>
            <h3 className="font-display text-xl font-bold text-gray-900 tracking-tight uppercase">Threshold</h3>
            <p className="font-body text-[var(--color-threshold)] text-sm">Your Healing, Witnessed</p>
          </div>
        </div>
        <span className="text-[var(--color-threshold)] font-body text-xs font-semibold uppercase tracking-wide">Coming Soon</span>
      </div>
      
      {/* Content */}
      <div>
        <h2 className="font-display text-3xl font-bold text-gray-900 mb-1">Your Therapy Journey,</h2>
        <h2 className="font-display text-3xl font-bold text-[var(--color-threshold)] mb-4">Mapped</h2>
        
        <p className="font-body text-gray-600 leading-relaxed mb-3">A private companion for people undergoing psychedelic-assisted therapy — including ketamine, psilocybin, and MDMA protocols.</p>
        <p className="font-body text-gray-500 text-sm mb-4">A private space to understand your healing journey. We're glad you're here.</p>
        
        {/* Quote */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-1 h-full bg-gray-300 rounded-full flex-shrink-0" />
          <p className="font-body text-gray-500 text-sm italic">Built from 7 years of sitting with patients before, during, and after ketamine sessions — and seeing how much the journey between sessions matters.</p>
        </div>
        
        <p className="font-body text-[var(--color-threshold)] text-sm mb-4 italic">with Haven</p>
        
        {/* Show More/Less Toggle */}
        <button 
          onClick={toggleExpanded} 
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-threshold)]/30 text-[var(--color-threshold)] font-body text-sm font-semibold hover:bg-[var(--color-threshold)]/5 transition-colors mb-4"
        >
          {expanded ? 'Show Less' : 'Show More'}
          <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
        </button>
        
        {/* Expandable Content */}
        {expanded && (
          <div className="space-y-8">
            {/* How It Works */}
            <div>
              <h4 className="font-display text-lg font-bold text-gray-900 mb-4">How It Works</h4>
              <div className="space-y-3">
                {features.map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-sm flex-shrink-0">{f.icon}</div>
                    <div>
                      <p className="font-body text-sm font-semibold text-gray-900">{f.name}</p>
                      <p className="font-body text-xs text-gray-500">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Supported Protocols */}
            <div>
              <h4 className="font-display text-lg font-bold text-gray-900 mb-3">Supported Protocols</h4>
              <div className="flex flex-wrap gap-2">
                {protocols.map((p, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full bg-white border border-gray-200 font-body text-sm text-[var(--color-threshold)]">{p}</span>
                ))}
              </div>
            </div>
            
            {/* Summary Box */}
            <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
              <p className="font-body text-gray-600 text-sm leading-relaxed">Threshold structures your entire treatment arc into meaningful check-ins — tracking your progress from preparation through integration, session by session.</p>
            </div>
          </div>
        )}
        
        {/* Footer */}
        <p className="font-body text-xs text-gray-400 uppercase tracking-wide mt-4 mb-3">Mind Relief Studios, LLC</p>
        
        {/* CTA */}
        <div className="flex items-center gap-4">
          <a href="#contact" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--color-threshold)] text-white rounded-full font-body text-sm font-semibold hover:bg-[var(--color-threshold)]/90 transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            Notify Me
          </a>
        </div>
      </div>
    </div>
  );
};

// Micra Card
const MicraCard: React.FC<{ activeApp: string | null; setActiveApp: (app: string | null) => void }> = ({ activeApp, setActiveApp }) => {
  const expanded = activeApp === 'micra';
  const toggleExpanded = () => setActiveApp(expanded ? null : 'micra');
  
  const features = [
    { name: 'Protocol Tracking', desc: 'Fadiman, Stamets, and custom schedules with dose logging in grams or milligrams', icon: '📋' },
    { name: 'Dose vs Off Day Insights', desc: 'See exactly how your mood, energy, and focus differ on dose days versus rest days', icon: '📊' },
    { name: 'Biometric Integration', desc: 'HRV and sleep data via Apple Health — automatically compared against your protocol', icon: '❤️' },
    { name: 'Completely Private', desc: 'No account. No cloud. No subscription. Your data stays yours.', icon: '🔒' },
  ];
  
  const protocols = ['Psilocybin', 'LSD', 'Ketamine', 'Custom'];
  
  return (
    <div className="app-card group relative p-6 md:p-8 rounded-3xl bg-gradient-to-br from-[#F0F9F4] to-[#E8F5EF] border border-[var(--color-micra)]/20 overflow-hidden self-start">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <img src="/icon-micra.png" alt="Micra" className="w-14 h-14 rounded-2xl" />
          <div>
            <h3 className="font-display text-xl font-bold text-gray-900 tracking-tight uppercase">Micra</h3>
            <p className="font-body text-[var(--color-micra)] text-sm">Microdosing Protocol Tracker</p>
          </div>
        </div>
        <span className="text-[var(--color-micra)] font-body text-xs font-semibold uppercase tracking-wide">Coming Soon</span>
      </div>
      
      {/* Content */}
      <div>
        <h2 className="font-display text-3xl font-bold text-gray-900 mb-1">Your Protocol.</h2>
        <h2 className="font-display text-3xl font-bold text-[var(--color-micra)] mb-4">Your Patterns.</h2>
        
        <p className="font-body text-gray-600 leading-relaxed mb-3">A private tracking companion for people following structured microdosing protocols — including psilocybin, LSD, and ketamine. Track your doses, mood, and biometrics. Then see what's actually working.</p>
        <p className="font-body text-gray-500 text-sm mb-4">A private space to understand what microdosing is doing for you. Your data stays on your device.</p>
        
        {/* Quote */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-1 h-full bg-gray-300 rounded-full flex-shrink-0" />
          <p className="font-body text-gray-500 text-sm italic">Built from clinical curiosity about what patients were actually experiencing between sessions — and realizing no tool existed to track it properly.</p>
        </div>
        
        <p className="font-body text-[var(--color-micra)] text-sm mb-4 italic">from Mind Relief Studios, LLC</p>
        
        {/* Show More/Less Toggle */}
        <button 
          onClick={toggleExpanded} 
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-micra)]/30 text-[var(--color-micra)] font-body text-sm font-semibold hover:bg-[var(--color-micra)]/5 transition-colors mb-4"
        >
          {expanded ? 'Show Less' : 'Show More'}
          <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
        </button>
        
        {/* Expandable Content */}
        {expanded && (
          <div className="space-y-8">
            {/* Key Features */}
            <div>
              <h4 className="font-display text-lg font-bold text-gray-900 mb-4">Key Features</h4>
              <div className="space-y-3">
                {features.map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-sm flex-shrink-0">{f.icon}</div>
                    <div>
                      <p className="font-body text-sm font-semibold text-gray-900">{f.name}</p>
                      <p className="font-body text-xs text-gray-500">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Supported Protocols */}
            <div>
              <h4 className="font-display text-lg font-bold text-gray-900 mb-3">Supported Protocols</h4>
              <div className="flex flex-wrap gap-2">
                {protocols.map((p, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full bg-white border border-gray-200 font-body text-sm text-[var(--color-micra)]">{p}</span>
                ))}
              </div>
            </div>
            
            {/* Summary Box */}
            <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
              <p className="font-body text-gray-600 text-sm leading-relaxed">Micra structures your protocol into meaningful check-ins — tracking your mood, energy, and biometrics from your first dose through your longest streak.</p>
            </div>
          </div>
        )}
        
        {/* Footer */}
        <p className="font-body text-xs text-gray-400 uppercase tracking-wide mt-4 mb-3">Mind Relief Studios, LLC</p>
        
        {/* CTA */}
        <div className="flex items-center gap-4">
          <a href="#contact" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--color-micra)] text-white rounded-full font-body text-sm font-semibold hover:bg-[var(--color-micra)]/90 transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            Notify Me
          </a>
        </div>
      </div>
    </div>
  );
};

export default App;
